import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DashboardRequestError } from "../../providers/aws/client";
import { fetchArtifact, fetchEvidence } from "./api";
import {
  artifactSchema,
  photoSchema,
  videoSchema,
  sections,
  type Artifact,
  type EvidencePage,
  type Section,
} from "./contracts";
import { PhotoEvidence } from "./PhotoEvidence";
import { VideoEvidence } from "./VideoEvidence";

function errorMessage(error: Error) {
  if (error instanceof DashboardRequestError) {
    if (error.status === 409)
      return "The spatial revision changed. Refresh to inspect the current evidence.";
    if (error.status === 404)
      return "Spatial evidence is disabled, not yet indexed, or no longer available.";
    if (error.status === 403) return "Administrator access is required.";
  }
  return "Spatial evidence could not be verified. Refresh to try again.";
}
export function EvidenceContent({
  page,
  onArtifact,
  onReference,
}: {
  page: EvidencePage;
  onArtifact: (artifact: Artifact) => void;
  onReference?: (assetId: string) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Badge variant="outline">
          {page.is_correction_draft
            ? "Correction draft"
            : page.sealed
              ? "Sealed for generation"
              : "Analysis revision"}
        </Badge>
        <Badge variant="outline">
          Revision {page.revision_id.slice(0, 12)}
        </Badge>
        <span>
          {page.total} {page.section}
        </span>
      </div>
      {page.is_correction_draft ? (
        <p className="text-sm">
          These corrections have not changed the generated Echo. Generated
          revision: {page.generated_revision_id.slice(0, 12)}.
        </p>
      ) : null}
      {!page.layout_available ? (
        <p role="status">The plan layout is unavailable for this revision.</p>
      ) : null}
      {page.pending_asset_ids.length ? (
        <p role="status">
          {page.pending_asset_ids.length} assets remain unresolved or pending.
        </p>
      ) : null}
      {page.diagnostics.length ? (
        <details>
          <summary>Diagnostics ({page.diagnostics.length})</summary>
          <ul className="list-disc pl-5 text-sm">
            {page.diagnostics.map((code, i) => (
              <li key={i}>{code}</li>
            ))}
          </ul>
        </details>
      ) : null}
      <div className="space-y-3">
        {page.items.map((item, index) =>
          page.section === "photos" ? (
            <PhotoEvidence
              key={String(item.asset_id)}
              photo={photoSchema.parse(item)}
              onArtifact={onArtifact}
              onReference={onReference}
            />
          ) : page.section === "videos" ? (
            <VideoEvidence
              key={String(item.asset_id)}
              video={videoSchema.parse(item)}
              onArtifact={onArtifact}
              onReference={onReference}
            />
          ) : (
            <article key={index} className="rounded-md border p-3">
              <strong>
                {String(
                  item.label ??
                    item.portal_id ??
                    item.pair_id ??
                    item.schema_version ??
                    page.section,
                )}
              </strong>
              {page.section === "portals" ? (
                <p className="text-sm">
                  {String(item.relation)} · {String(item.observed_kind)} ·{" "}
                  {String(item.observed_state)} ·{" "}
                  {item.accepted === true
                    ? "Verified passage"
                    : "Passage not verified"}
                </p>
              ) : null}
              <details>
                <summary>Inspect evidence details</summary>
                <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-all text-xs">
                  {JSON.stringify(item, null, 2)}
                </pre>
              </details>
              {artifactSchema.safeParse(item.artifact).success ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    onArtifact(artifactSchema.parse(item.artifact))
                  }
                >
                  Inspect source JSON
                </Button>
              ) : null}
            </article>
          ),
        )}
      </div>
      {!page.items.length ? (
        <p>No {page.section} are recorded for this revision.</p>
      ) : null}
      <details>
        <summary>Configuration and source artifacts</summary>
        <dl className="space-y-2 break-all text-xs">
          <div>
            <dt>Frozen per-Echo configuration reference</dt>
            <dd>{page.echo_config.config_ref_digest}</dd>
          </div>
          <div>
            <dt>Frozen per-Echo spatial policy</dt>
            <dd>{page.echo_config.policy_digest}</dd>
          </div>
          <div>
            <dt>Verified evidence-access release</dt>
            <dd>
              {page.evidence_config.release_id} · {page.evidence_config.digest}
            </dd>
          </div>
        </dl>
        <p className="my-2 text-xs text-muted-foreground">
          This view verifies the evidence-access snapshot and displays the
          generation binding. Desired configuration and deployed service
          readbacks require the Runtime Configuration view.
        </p>
        <div className="flex flex-wrap gap-2">
          {page.artifacts.map((artifact) => (
            <Button
              variant="outline"
              size="sm"
              key={artifact.sha256}
              onClick={() => onArtifact(artifact)}
            >
              {artifact.artifact_schema}
            </Button>
          ))}
        </div>
      </details>
      {page.history_truncated ? (
        <p className="text-xs">
          The event history is partial. The current revision was read from its
          committed storage head.
        </p>
      ) : null}
    </div>
  );
}
export function SpatialEvidenceCard({
  sessionId,
  workloadId,
}: {
  sessionId: string;
  workloadId?: string | null;
}) {
  const [enabled, setEnabled] = useState(false);
  const [section, setSection] = useState<Section>("photos");
  const [assetId, setAssetId] = useState<string>();
  const [cursors, setCursors] = useState<string[]>([]);
  const [selected, setSelected] = useState<Artifact | null>(null);
  const [epoch, setEpoch] = useState(0);
  const cursor = cursors.at(-1);
  const query = useQuery({
    queryKey: [
      "spatial-evidence",
      sessionId,
      workloadId,
      assetId,
      section,
      cursor,
      epoch,
    ],
    queryFn: ({ signal }) =>
      fetchEvidence(
        {
          sessionId,
          workload: workloadId ?? undefined,
          section,
          cursor,
          assetId,
        },
        signal,
      ),
    enabled,
    retry: false,
    gcTime: 0,
    staleTime: 0,
  });
  const artifact = useQuery({
    queryKey: [
      "spatial-evidence",
      "artifact",
      sessionId,
      query.data?.revision_id,
      selected?.sha256,
    ],
    queryFn: ({ signal }) =>
      fetchArtifact(
        {
          sessionId,
          workload: workloadId ?? undefined,
          revision: query.data!.revision_id,
          sha256: selected!.sha256,
        },
        signal,
      ),
    enabled: Boolean(selected && query.data && !query.isError),
    retry: false,
    gcTime: 0,
    staleTime: 0,
  });
  function refresh() {
    setCursors([]);
    setSelected(null);
    setEpoch((value) => value + 1);
  }
  return (
    <Card data-testid="spatial-evidence-card">
      <CardHeader>
        <CardTitle>Spatial evidence</CardTitle>
        <CardDescription>
          Room candidates, structural evidence, corrections, and transition
          validation.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!enabled ? (
          <Button variant="outline" onClick={() => setEnabled(true)}>
            Load spatial evidence
          </Button>
        ) : (
          <>
            <div
              className="flex flex-wrap gap-2"
              aria-label="Evidence categories"
            >
              {sections.map((value) => (
                <Button
                  size="sm"
                  variant={section === value ? "default" : "outline"}
                  key={value}
                  onClick={() => {
                    setSection(value);
                    setAssetId(undefined);
                    setCursors([]);
                    setSelected(null);
                  }}
                >
                  {value[0].toUpperCase() + value.slice(1)}
                </Button>
              ))}
              <Button size="sm" variant="outline" onClick={refresh}>
                Refresh
              </Button>
            </div>
            {query.isFetching ? (
              <p role="status">Verifying spatial evidence…</p>
            ) : query.isError ? (
              <p role="alert">{errorMessage(query.error)}</p>
            ) : query.data ? (
              <>
                {assetId ? (
                  <p className="text-sm">
                    Inspecting reference {assetId}.{" "}
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => {
                        setAssetId(undefined);
                        setCursors([]);
                        setSelected(null);
                      }}
                    >
                      Show all {section}
                    </Button>
                  </p>
                ) : null}
                <EvidenceContent
                  page={query.data}
                  onArtifact={setSelected}
                  onReference={(asset) => {
                    setAssetId(asset);
                    setSection(section === "videos" ? "videos" : "photos");
                    setCursors([]);
                    setSelected(null);
                  }}
                />
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    disabled={!cursors.length}
                    onClick={() => {
                      setCursors((values) => values.slice(0, -1));
                      setSelected(null);
                    }}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    disabled={!query.data.next_cursor}
                    onClick={() => {
                      setCursors((values) => [
                        ...values,
                        query.data!.next_cursor!,
                      ]);
                      setSelected(null);
                    }}
                  >
                    Next
                  </Button>
                </div>
              </>
            ) : null}
            {selected && !query.isError && !query.isFetching ? (
              <section
                aria-label="Verified spatial artifact"
                className="space-y-2 rounded-md border p-3"
              >
                <div className="flex items-center justify-between">
                  <strong>{selected.artifact_schema}</strong>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelected(null)}
                  >
                    Close JSON
                  </Button>
                </div>
                <p className="break-all text-xs">
                  SHA-256: {selected.sha256} · Object version:{" "}
                  {selected.version_id}
                </p>
                {artifact.isFetching ? (
                  <p role="status">Verifying artifact…</p>
                ) : artifact.isError ? (
                  <p role="alert">{errorMessage(artifact.error)}</p>
                ) : (
                  <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-all text-xs">
                    {JSON.stringify(artifact.data?.data, null, 2)}
                  </pre>
                )}
              </section>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}
